/*
  사원증 결제정보 엑셀 파싱

  다운로드 양식은 2번째 탭에 데이터가 있고, 그 탭은 [제목 → 빈 줄 → 헤더 → 데이터] 순이다.
  헤더 행은 아래와 같다.
    # | 개인식별 번호 | ID | 이름 | 식대그룹 | 부서 | 사원번호 | 사용자 상태 | 가입일
      | 현재 사원증 결제정보 | 변경할 사원증 결제정보
  헤더에는 '(수정 불가)' / '(중복 불가)' 같은 꼬리말이 붙어 있어 비교 전에 괄호 부분을 떼어낸다.
  제목/빈 줄은 헤더 행을 직접 찾아 건너뛰므로 위치가 조금 달라져도 읽힌다.

  저장(PUT/DELETE /company/v2/barCode/member/{userId})이 userId 를 경로로 요구하므로
  화면에 보이는 ID(signId) 와 별개로 '개인식별 번호' 열을 userId 로 읽는다.

  보낼 수 없거나 보낼 것이 없는 행은 미리보기에서 제외한다.
  '현재'와 '변경할' 값이 같은 행, 그리고 저장 경로를 만들 수 없는(개인식별 번호가 빈) 행이 그렇다.
  두 결제정보 열 중 하나라도 없으면 양식이 틀린 것이라 파일 전체를 받지 않는다.

  두 결제정보 열은 읽을 때 정규화(공백 제거·하이픈 치환·대문자 변환)한다.
  미리보기에 저장될 값이 그대로 보이고, 변경 여부·중복·글자수도 정규화한 값으로 판정된다.
*/
import * as XLSX from 'xlsx';

import { EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH, normalizeEmployeeCardPaymentCode } from 'constants/employeeCardPayment';

const HEADER_SCAN_ROW_LIMIT = 50;

/*
  헤더 텍스트(괄호 꼬리말 제거 + 공백 제거 + 소문자)로 열을 판정한다.
  '개인식별 번호'와 'ID'는 부분일치로 구분되지 않으므로 정확히 비교한다.
  결제정보 열도 둘이라 '현재'/'변경할' 접두사로 가른다.
  순서대로 검사하고, 이미 배정된 열/키는 건너뛴다.
  마지막 barCode 항목은 '변경할' 로 시작하지 않는 결제정보 열을 위한 폴백이다.
*/
const COLUMN_MATCHERS = [
  { key: 'excelIndex', test: (header) => ['#', 'no', 'no.', '순번', 'index'].indexOf(header) > -1 },
  { key: 'userId', test: (header) => ['개인식별번호', 'userid', '사용자id'].indexOf(header) > -1 },
  { key: 'signId', test: (header) => ['id', 'signid', '아이디', '로그인id'].indexOf(header) > -1 },
  { key: 'name', test: (header) => ['이름', '성명'].indexOf(header) > -1 },
  { key: 'currentBarCode', test: (header) => header.indexOf('현재') === 0 && header.indexOf('결제정보') > -1 },
  { key: 'barCode', test: (header) => header.indexOf('변경') === 0 && header.indexOf('결제정보') > -1 },
  { key: 'barCode', test: (header) => header.indexOf('결제정보') > -1 || header.indexOf('바코드') > -1 }
];

// 헤더 행을 못 찾았을 때의 최후 수단 — 양식의 고정 열 위치와 데이터 시작 행(제목 → 빈 줄 → 헤더 다음).
const TEMPLATE_COLUMN_INDEXES = { excelIndex: 0, userId: 1, signId: 2, name: 3, currentBarCode: 9, barCode: 10 };
const TEMPLATE_START_ROW = 3;

const DATA_SHEET_INDEX = 1;

// 모든 열 공용(앞뒤 공백만 뗀다). 결제정보 열은 normalizeEmployeeCardPaymentCode 를 쓴다 — ID·이름까지 대문자로 바뀌면 안 된다.
const normalize = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const normalizeHeader = (value) =>
  normalize(value)
    .replace(/\([^)]*\)/g, '') // '(수정 불가)' 같은 꼬리말 제거
    .replace(/\s/g, '')
    .toLowerCase();

const findColumnIndexes = (headerRow) => {
  const indexes = {};
  const takenColumns = {};

  COLUMN_MATCHERS.forEach(({ key, test }) => {
    if (indexes[key] !== undefined) return;

    headerRow.some((cell, columnIndex) => {
      if (takenColumns[columnIndex]) return false;

      const header = normalizeHeader(cell);
      if (!header || !test(header)) return false;

      indexes[key] = columnIndex;
      takenColumns[columnIndex] = true;
      return true;
    });
  });

  return indexes;
};

// 헤더 행을 찾는다. ID 와 이름이 같은 행에 있으면 그 행을 헤더로 본다.
const findHeaderRowIndex = (rows) => {
  const limit = Math.min(rows.length, HEADER_SCAN_ROW_LIMIT);

  for (let i = 0; i < limit; i += 1) {
    const indexes = findColumnIndexes(rows[i] || []);
    if (indexes.signId !== undefined && indexes.name !== undefined) {
      return i;
    }
  }

  return -1;
};

/*
  업로드 직후(전송 전) 검증.
  - 파일 안에서 개인식별 번호(userId)가 중복된 행 — 저장 키가 userId 라 같은 사람을 두 번 쓰게 된다
  - 파일 안에서 사원증 결제정보가 중복된 행 — 회사 내 중복 불가
  - 사원증 결제정보가 최대 길이를 넘는 행
  위반 사유는 비고 컬럼에 그대로 노출되도록 message 에 담는다.
*/
const countBy = (rows, pick) =>
  rows.reduce((acc, row) => {
    const key = pick(row);
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

/**
 * @param rows        전송 대상(= 변경된) 행
 * @param keptBarCodes 바꾸지 않아 그대로 남는 행들의 현재 사원증 결제정보.
 *                     변경된 행이 이 값과 같으면 저장 시 회사 내 중복이 된다.
 */
export const validateRows = (rows, keptBarCodes = []) => {
  const userIdCount = countBy(rows, (row) => row.userId);
  // 빈 값(삭제)은 여러 행에 있어도 중복이 아니다.
  const barCodeCount = countBy(rows, (row) => row.barCode);
  const keptBarCodeSet = keptBarCodes.reduce((acc, code) => {
    if (code) acc[code] = true;
    return acc;
  }, {});

  return rows.map((row) => {
    const messages = [];

    if (row.userId && userIdCount[row.userId] > 1) {
      messages.push('엑셀파일 내 중복된 개인식별 번호가 있습니다.');
    }
    if (row.barCode && (barCodeCount[row.barCode] > 1 || keptBarCodeSet[row.barCode])) {
      messages.push('엑셀파일 내 중복된 사원증 결제정보가 있습니다.');
    }
    if (row.barCode.length > EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH) {
      messages.push(`사원증 결제정보는 최대 ${EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH}자까지 입력 가능합니다.`);
    }

    return { ...row, isValid: !messages.length, message: messages.join(' / ') };
  });
};

/*
  열이 통째로 없는 것과 칸만 비어 있는 것은 뜻이 다르다.
  빈 칸은 '지운다'는 의사표시지만, 열이 없는 건 양식이 틀린 것이다.
  같게 다루면 파일 하나로 전원 삭제가 걸리거나 바뀌지도 않은 행을 전부 보내게 된다.
*/
const REQUIRED_COLUMNS = [
  // 저장 경로(/member/{userId})를 만드는 열. 없으면 전 행이 '보낼 수 없음'이 되어 변경 없음처럼 보인다.
  { key: 'userId', label: '개인식별 번호' },
  { key: 'currentBarCode', label: '현재 사원증 결제정보' },
  { key: 'barCode', label: '변경할 사원증 결제정보' }
];

// 화면이 사유를 그대로 띄울 수 있도록 일반 파싱 실패와 구분되는 표시를 단다.
const formatError = (message) => {
  const error = new Error(message);

  error.isFormatError = true;
  return error;
};

const toPreviewRows = (rows) => {
  const headerRowIndex = findHeaderRowIndex(rows);
  const indexes = headerRowIndex > -1 ? findColumnIndexes(rows[headerRowIndex]) : TEMPLATE_COLUMN_INDEXES;
  const startRow = headerRowIndex > -1 ? headerRowIndex + 1 : TEMPLATE_START_ROW;

  // 값과 비교 기준이라 하나라도 없으면 무엇을 바꾸려는 건지 알 수 없다. 읽기 전에 끊는다.
  const missingColumns = REQUIRED_COLUMNS.filter((column) => indexes[column.key] === undefined);

  if (missingColumns.length) {
    const labels = missingColumns.map((column) => `[${column.label}]`).join(' ');

    throw formatError(`${labels} 열을 찾을 수 없습니다. 다운로드한 양식을 그대로 사용해 주세요.`);
  }

  const previewRows = [];

  for (let i = startRow; i < rows.length; i += 1) {
    const row = rows[i] || [];
    // 화면의 No. 는 엑셀 '#' 열 값을 그대로 쓴다(변경된 행만 남겨도 원본 번호를 잃지 않도록).
    const excelIndex = indexes.excelIndex === undefined ? '' : normalize(row[indexes.excelIndex]);
    const userId = indexes.userId === undefined ? '' : normalize(row[indexes.userId]);
    const signId = indexes.signId === undefined ? '' : normalize(row[indexes.signId]);
    const name = indexes.name === undefined ? '' : normalize(row[indexes.name]);
    // '현재' 도 같은 기준으로 맞춰야 대소문자·공백만 다른 행이 '변경 없음'으로 빠진다.
    const barCode = normalizeEmployeeCardPaymentCode(row[indexes.barCode]);
    const currentBarCode = normalizeEmployeeCardPaymentCode(row[indexes.currentBarCode]);

    // 완전히 빈 행은 건너뛴다(행 자체가 지워진 사용자는 작업 범위 제외).
    if (signId || name || barCode || currentBarCode) {
      previewRows.push({ rowNumber: i + 1, excelIndex, userId, signId, name, currentBarCode, barCode });
    }
  }

  /*
    전송 대상은 '현재' 와 '변경할' 이 다른 행이다.
    개인식별 번호가 없으면 저장 경로(/member/{userId})를 만들 수 없어 보낼 방법이 아예 없으므로,
    값을 바꿨더라도 변경 없는 행과 똑같이 건너뛴다.
  */
  const isSendable = (row) => !!row.userId && row.currentBarCode !== row.barCode;

  const changedRows = previewRows.filter(isSendable);

  // 보내지 않는 행의 값은 저장 후에도 그대로 남으므로 중복 판정에 포함해야 한다.
  const keptBarCodes = previewRows.filter((row) => !isSendable(row)).map((row) => row.currentBarCode);

  return validateRows(changedRows, keptBarCodes);
};

const readSheetRows = (workbook, sheetName) => {
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) return [];

  // blankrows 를 유지해야 rowNumber 가 엑셀의 실제 행 번호와 어긋나지 않는다.
  return XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: true, defval: '' });
};

/*
  양식은 2번째 탭에 데이터가 있다. 탭 순서가 바뀌어도 읽히도록
  데이터 탭부터 시작해 헤더 행이 있는 탭을 찾고, 없으면 데이터 탭을 그대로 쓴다.
*/
const readDataSheetRows = (workbook) => {
  const names = workbook.SheetNames || [];
  const order = [];

  for (let i = DATA_SHEET_INDEX; i < names.length; i += 1) order.push(i);
  for (let i = 0; i < DATA_SHEET_INDEX && i < names.length; i += 1) order.push(i);

  for (let i = 0; i < order.length; i += 1) {
    const rows = readSheetRows(workbook, names[order[i]]);
    if (findHeaderRowIndex(rows) > -1) return rows;
  }

  return readSheetRows(workbook, names[DATA_SHEET_INDEX] || names[0]);
};

export const readEmployeeCardPaymentExcel = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'array' });

        resolve(toPreviewRows(readDataSheetRows(workbook)));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

export default { readEmployeeCardPaymentExcel, validateRows };
